"use client";

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCart } from '@/context/CartContext';
import { useStore } from '@/context/StoreContext';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency } from '@/lib/formatters';
import { getSubdomain } from '@/lib/subdomain';
import { getApiBaseUrl } from '@/lib/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartShopping, faUser, faArrowLeft, faRightFromBracket, faBoxOpen, faSpinner } from '@fortawesome/free-solid-svg-icons';

// Ícones SVG Nativos
const SearchIcon = (props: React.SVGProps<SVGSVGElement>) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg>);
const Bars3Icon = (props: React.SVGProps<SVGSVGElement>) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>);

interface HeaderProps {
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    showBackButton?: boolean;
}

export default function Header({ searchValue, onSearchChange, showBackButton = false }: HeaderProps) {
    const { openCart, totalItems } = useCart();
    const { settings } = useStore();
    const { user, isAuthenticated, logout } = useAuth();
    const router = useRouter();
    
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [localSearch, setLocalSearch] = useState('');
    const [quickResults, setQuickResults] = useState<any[]>([]);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchContainerRef = useRef<HTMLDivElement>(null);

    const storeName = settings.store_name || "Loja Virtual";
    const displaySearch = searchValue !== undefined ? searchValue : localSearch;

    // Fecha o dropdown se clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Busca rápida instantânea
    useEffect(() => {
        if (onSearchChange) return; // Se a página atual já gerencia a busca (ex: /buscar), não abre dropdown
        const term = localSearch.trim();
        if (term.length < 2) {
            setQuickResults([]);
            setShowDropdown(false);
            return;
        }

        const timer = setTimeout(async () => {
            setIsLoadingSearch(true);
            try {
                const subdomain = getSubdomain();
                const apiBaseUrl = getApiBaseUrl();
                const res = await fetch(`${apiBaseUrl}/public/products?search=${encodeURIComponent(term)}&per_page=5`, {
                    headers: { 'X-Tenant-Subdomain': subdomain, 'Accept': 'application/json' }
                });
                if (res.ok) {
                    const data = await res.json();
                    setQuickResults(data.data || []);
                    setShowDropdown(true);
                }
            } catch (e) {
                console.error("Erro na busca rápida:", e);
            } finally {
                setIsLoadingSearch(false);
            }
        }, 250);

        return () => clearTimeout(timer);
    }, [localSearch, onSearchChange]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (onSearchChange) onSearchChange(val);
        else setLocalSearch(val);
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setShowDropdown(false);
        if (!onSearchChange && localSearch.trim()) {
            router.push(`/buscar?search=${encodeURIComponent(localSearch.trim())}`);
        }
    };

    // Pega o primeiro nome do usuário
    const firstName = user?.name ? user.name.split(' ')[0] : '';

    return (
        <>
            <header className="header-container bg-[var(--secondary,#ffffff)] shadow-sm border-b border-gray-100 sticky top-0 z-40">
                <div className="container mx-auto px-4 py-4 flex items-center justify-between gap-4 md:gap-8">
                    
                    {/* 1. LOGO E MENU MOBILE / VOLTAR */}
                    <div className="flex items-center gap-3 w-auto md:w-1/4">
                        {showBackButton ? (
                            <button onClick={() => router.back()} className="flex items-center gap-2 text-gray-600 hover:text-[var(--primary)] transition-colors font-medium">
                                <FontAwesomeIcon icon={faArrowLeft} />
                                <span className="hidden sm:inline">Voltar</span>
                            </button>
                        ) : (
                            <button onClick={() => setIsMenuOpen(true)} className="md:hidden text-gray-600 hover:text-[var(--primary)] transition-colors">
                                <Bars3Icon className="w-8 h-8" />
                            </button>
                        )}
                        
                        <div className="flex items-center gap-2.5">
                            <Link href="/" className="flex-shrink-0 flex flex-col justify-center">
                                {settings.logo_url ? (
                                    <img src={settings.logo_url} alt={storeName} className="h-8 md:h-12 w-auto object-contain" />
                                ) : (
                                    <span className="text-2xl font-black text-[var(--primary)] tracking-tight">{storeName}</span>
                                )}
                                {settings.slogan && (
                                    <span className="text-[10px] text-gray-500 font-medium hidden sm:block truncate max-w-[200px]">
                                        {settings.slogan}
                                    </span>
                                )}
                            </Link>

                        </div>
                    </div>

                    {/* 2. BARRA DE BUSCA CENTRAL COM AUTOCOMPLETE FLUTUANTE */}
                    <div className="hidden md:flex flex-1 max-w-2xl relative" ref={searchContainerRef}>
                        <form onSubmit={handleSearchSubmit} className="w-full relative">
                            <input 
                                type="text" 
                                placeholder="Estou buscando por..." 
                                className="w-full bg-gray-100/80 border-2 border-transparent text-gray-700 rounded-full py-3 pl-6 pr-14 outline-none focus:bg-white focus:border-[var(--primary)] focus:shadow-md transition-all duration-300"
                                value={displaySearch} 
                                onChange={handleInputChange}
                                onFocus={() => { if (quickResults.length > 0) setShowDropdown(true); }}
                            />
                            <button type="submit" className="absolute right-1 top-1/2 -translate-y-1/2 bg-[var(--primary)] text-white w-10 h-10 rounded-full flex items-center justify-center hover:brightness-110 shadow-sm transition-all active:scale-95">
                                {isLoadingSearch ? <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-5 h-5"/>}
                            </button>
                        </form>

                        {/* Dropdown de Resultados Rápidos */}
                        {showDropdown && quickResults.length > 0 && (
                            <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 p-2 z-50 animate-fadeIn">
                                <div className="text-[11px] font-bold text-gray-400 px-3 py-1.5 uppercase tracking-wider flex justify-between items-center">
                                    <span>Produtos Encontrados</span>
                                    {isLoadingSearch && <FontAwesomeIcon icon={faSpinner} className="animate-spin text-[var(--primary)]" />}
                                </div>
                                <div className="divide-y divide-gray-50 max-h-80 overflow-y-auto">
                                    {quickResults.map((item) => (
                                        <Link
                                            key={item.id}
                                            href={`/produtos/${item.id}`}
                                            onClick={() => setShowDropdown(false)}
                                            className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-xl transition-colors group/item"
                                        >
                                            <div className="w-11 h-11 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                                                <img 
                                                    src={item.image_url || 'https://placehold.co/100'} 
                                                    alt={item.name} 
                                                    className="w-full h-full object-cover group-hover/item:scale-105 transition-transform" 
                                                />
                                            </div>
                                            <div className="flex-grow min-w-0">
                                                <h4 className="text-xs font-bold text-gray-800 truncate group-hover/item:text-[var(--primary)] transition-colors">
                                                    {item.name}
                                                </h4>
                                                <span className="text-[10px] text-gray-400 block truncate">
                                                    {item.category?.name || 'Geral'}
                                                </span>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <span className="text-xs font-black text-[var(--primary)]">
                                                    {formatCurrency(item.starting_price, settings.currency)}
                                                </span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowDropdown(false);
                                        router.push(`/buscar?search=${encodeURIComponent(localSearch.trim())}`);
                                    }}
                                    className="w-full text-center py-2 text-xs font-bold text-[var(--primary)] hover:bg-[var(--primary)]/5 rounded-xl transition-colors mt-1"
                                >
                                    Ver todos os resultados para "{localSearch}" →
                                </button>
                            </div>
                        )}
                    </div>

                    {/* 3. AÇÕES (CONTA E CARRINHO) */}
                    <div className="flex items-center justify-end gap-2 md:gap-6 w-auto md:w-1/4">
                        
                        {/* Botão de Conta (Desktop) */}
                        <Link href="/minha-conta" className="hidden lg:flex items-center gap-3 group cursor-pointer">
                            <div className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all overflow-hidden ${isAuthenticated ? 'bg-[var(--primary)]/10 text-[var(--primary)] border-[var(--primary)]/30' : 'bg-gray-50 text-gray-500 border-gray-200 group-hover:text-[var(--primary)] group-hover:border-[var(--primary)]'}`}>
                                {isAuthenticated && user?.avatar_url ? (
                                    <img src={user.avatar_url} alt={user.name} className="w-full h-full object-cover" />
                                ) : (
                                    <FontAwesomeIcon icon={faUser} className="text-lg" />
                                )}
                            </div>
                            <div className="flex flex-col text-sm">
                                <span className="text-gray-400 text-xs font-medium">{isAuthenticated ? `Olá, ${firstName}` : 'Bem-vindo(a)'}</span>
                                <span className="font-bold text-gray-800 group-hover:text-[var(--primary)] transition-colors leading-tight">{isAuthenticated ? 'Meus Pedidos' : 'Entrar / Cadastrar'}</span>
                            </div>
                        </Link>

                        {/* Botão de Conta (Mobile) */}
                        <Link href="/minha-conta" className="lg:hidden p-2 text-gray-600 hover:text-[var(--primary)] transition-colors">
                            {isAuthenticated && user?.avatar_url ? (
                                <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-[var(--primary)]/30">
                                    <img src={user.avatar_url} alt={user.name} className="w-full h-full object-cover" />
                                </div>
                            ) : (
                                <FontAwesomeIcon icon={faUser} className="h-6 w-6" />
                            )}
                        </Link>

                        <div className="hidden lg:block w-px h-8 bg-gray-200"></div>

                        {/* Carrinho */}
                        <button onClick={openCart} className="flex items-center gap-3 group relative cursor-pointer">
                            <div className="w-10 h-10 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-500 group-hover:text-[var(--primary)] group-hover:border-[var(--primary)] transition-all">
                                <FontAwesomeIcon icon={faCartShopping} className="text-lg" />
                                {totalItems > 0 && (
                                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[11px] font-black rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                                        {totalItems}
                                    </span>
                                )}
                            </div>
                            <div className="hidden lg:flex flex-col text-sm text-left">
                                <span className="text-gray-400 text-xs font-medium">Meu Carrinho</span>
                                <span className="font-bold text-gray-800 group-hover:text-[var(--primary)] transition-colors leading-tight">
                                    {totalItems > 0 ? `${totalItems} itens` : 'Vazio'}
                                </span>
                            </div>
                        </button>
                    </div>
                </div>

                {/* BUSCA MOBILE */}
                <div className="md:hidden px-4 pb-4">
                    <form onSubmit={handleSearchSubmit} className="relative">
                        <input 
                            type="text" 
                            placeholder="Estou buscando por..." 
                            className="w-full bg-gray-100 border border-transparent rounded-xl py-3 pl-4 pr-12 text-sm focus:bg-white focus:border-[var(--primary)] outline-none transition-all shadow-sm"
                            value={displaySearch} 
                            onChange={handleInputChange} 
                        />
                        <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[var(--primary)] p-2">
                            <SearchIcon className="w-5 h-5"/>
                        </button>
                    </form>
                </div>
            </header>

            {/* MENU LATERAL MOBILE */}
            {isMenuOpen && (
                <div className="fixed inset-0 z-50 flex">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsMenuOpen(false)}></div>
                    <div className="relative bg-white w-4/5 max-w-xs h-full shadow-2xl p-6 flex flex-col animate-slideRight">
                        <div className="flex justify-between items-center mb-8">
                            <span className="font-bold text-lg text-[var(--primary)]">Menu</span>
                            <button onClick={() => setIsMenuOpen(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200">
                                <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4" />
                            </button>
                        </div>
                        
                        <nav className="space-y-4 text-lg font-medium text-gray-700 flex-grow">
                            <Link href="/" className="block hover:text-[var(--primary)] p-2 bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>Início</Link>
                            {!settings.ocultar_produtos && (
                                <Link href="/buscar" className="block hover:text-[var(--primary)] p-2 bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>Todos os Produtos</Link>
                            )}
                            {!settings.ocultar_reservas && (
                                <Link href="/#agendamentos" className="block hover:text-[var(--primary)] p-2 bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>Serviços & Agendamentos</Link>
                            )}
                            
                            {isAuthenticated ? (
                                <Link href="/minha-conta" className="flex items-center gap-2 hover:text-[var(--primary)] p-2 bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>
                                    <FontAwesomeIcon icon={faBoxOpen} className="text-gray-400" /> Meus Pedidos
                                </Link>
                            ) : (
                                <Link href="/minha-conta" className="block hover:text-[var(--primary)] p-2 bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>Minha Conta</Link>
                            )}
                        </nav>

                        {isAuthenticated && (
                            <div className="mt-auto border-t border-gray-100 pt-4">
                                <div className="flex items-center gap-3 mb-4 p-2">
                                    <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-200">
                                        {user?.avatar_url ? (
                                            <img src={user.avatar_url} alt={user.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-500 font-bold">
                                                {user?.name?.substring(0, 1).toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-sm font-bold text-gray-800 leading-tight">{firstName}</span>
                                        <span className="text-xs text-gray-500 truncate max-w-[150px]">{user?.email}</span>
                                    </div>
                                </div>

                                <button onClick={() => { logout(); setIsMenuOpen(false); }} className="flex items-center gap-2 text-red-500 font-bold p-2 w-full hover:bg-red-50 rounded-lg transition-colors">
                                    <FontAwesomeIcon icon={faRightFromBracket} /> Sair da Conta
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}